import {Form} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {GetCustomerPaymentApiResponse} from '@dms/api/finance';
import {GetCheckoutByIdApiResponse, Payment} from '@dms/api/sales';
import {useBank} from '@dms/shared';

import {suffixTestId} from 'shared/web';

import {IssueRestrictionResponse} from '../../AftersalesCheckoutPayment/types/IssueRestrictionResponse';
import {useCheckoutPaymentIssuing} from '../hooks/useCheckoutPaymentIssuing';
import type {CheckoutPaymentCardV2Props} from '../types/CheckoutPaymentCardV2Props';
import {
  checkoutPaymentFormSchemaV2,
  CheckoutPaymentFormTypeV2,
} from '../types/CheckoutPaymentFormTypeV2';
import {buildPaymentFormDefaultValues} from '../utils/buildPaymentFormDefaultValues';
import {CheckoutPaymentActionsV2} from './CheckoutPaymentActionsV2';
import {CheckoutPaymentFormBodyV2} from './CheckoutPaymentFormBodyV2';
import {CheckoutPaymentTemplateDropdownV2} from './CheckoutPaymentTemplateDropdownV2';
import {PaymentOverviewV2} from './PaymentOverviewV2';

interface CheckoutPaymentCardContentV2Props extends CheckoutPaymentCardV2Props {
  payment: Payment;
  checkout: GetCheckoutByIdApiResponse | undefined;
  customerPayment: GetCustomerPaymentApiResponse | undefined;
  issueRestrictions: IssueRestrictionResponse[] | undefined;
}

export function CheckoutPaymentCardContentV2(props: CheckoutPaymentCardContentV2Props) {
  const {primaryBankAccountOption} = useBank();

  const paymentData = props.payment;
  const checkoutData = props.checkout;

  const uiCapabilities = paymentData.uiCapabilities;
  const allowedActions = uiCapabilities?.allowedActions ?? [];
  const isEditMode = uiCapabilities?.mode === 'EDIT';

  const {handleFormChange, issuePayment, isLazySaving, isIssuing} = useCheckoutPaymentIssuing({
    checkoutId: props.checkoutId,
    paymentId: props.paymentId,
    payment: paymentData,
    checkout: checkoutData,
  });

  return (
    <>
      {isEditMode && isNotNil(uiCapabilities) ? (
        <Form<CheckoutPaymentFormTypeV2>
          mode="onSubmit"
          defaultValues={buildPaymentFormDefaultValues({
            payment: paymentData,
            customerPayment: props.customerPayment,
            defaultPaymentType: props.defaultPaymentType,
            primaryBankAccountValue: primaryBankAccountOption?.value ?? undefined,
          })}
          experimentalZodSchema={checkoutPaymentFormSchemaV2}
          onChange={handleFormChange}
          onSubmit={issuePayment}
          isDisabled={props.isEditingDisabled}
        >
          {(control, formApi) => (
            <VStack spacing={4}>
              <CheckoutPaymentFormBodyV2
                control={control}
                customerId={props.customerId}
                formApi={formApi}
                checkoutId={props.checkoutId}
                uiCapabilities={uiCapabilities}
                payment={paymentData}
                onSaveChanges={handleFormChange}
                totalAmount={checkoutData?.totalPrice?.withVat?.amount ?? 0}
                isEditingDisabled={props.isEditingDisabled}
                isForeignCurrencyAllowed={checkoutData?.isForeignCurrencyPayment}
                data-testid={suffixTestId('formBody', props)}
              />
              <CheckoutPaymentActionsV2
                allowedActions={allowedActions}
                checkoutId={props.checkoutId}
                payment={paymentData}
                isEditingDisabled={props.isEditingDisabled}
                isCompletingDisabled={props.isCompletingDisabled}
                templateDropdown={
                  <CheckoutPaymentTemplateDropdownV2
                    checkoutId={props.checkoutId}
                    payment={paymentData}
                    formApi={formApi}
                    onTemplateSelect={() => handleFormChange(formApi.getValues())}
                    data-testid={suffixTestId('template', props)}
                  />
                }
                isSubmitButtonDisabled={
                  isNilOrEmpty(checkoutData?.billingInformation?.customerInformation.id) ||
                  isNilOrEmpty(checkoutData?.billingInformation?.contractInformation?.id)
                }
                onAddAnotherDepositClick={props.onAddAnotherDepositClick}
                onRemoveDepositClick={props.onRemoveDepositClick}
                isBalancePaymentIssued={props.isBalancePaymentIssued}
                issueRestrictions={props.issueRestrictions}
                isIssueLoading={isIssuing}
                isLazySaving={isLazySaving}
                control={control}
                contextTarget={props.contextTarget}
                contextId={props.contextId}
                actionContext={props.actionContext}
                data-testid={suffixTestId('actions', props)}
              />
            </VStack>
          )}
        </Form>
      ) : null}
      <Show when={!isEditMode}>
        <PaymentOverviewV2
          paymentId={props.paymentId}
          checkoutId={props.checkoutId}
          customerId={props.customerId}
          payment={paymentData}
          issueRestrictions={props.issueRestrictions}
          isLazySaving={isLazySaving}
          isEditingDisabled={props.isEditingDisabled}
          isCompletingDisabled={props.isCompletingDisabled}
          onAddAnotherDepositClick={props.onAddAnotherDepositClick}
          onRemoveDepositClick={props.onRemoveDepositClick}
          isBalancePaymentIssued={props.isBalancePaymentIssued}
          contextTarget={props.contextTarget}
          contextId={props.contextId}
          actionContext={props.actionContext}
          data-testid={props['data-testid']}
        />
      </Show>
    </>
  );
}
